import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Wrench,
  Search,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  ChevronRight,
  Zap,
  Disc,
  Cpu,
  Layers,
  Thermometer,
  Car,
  Armchair,
  Fuel,
  CircleDot,
  Filter,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { appRoutes } from '@/constants'
import {
  PARTS_CATEGORIES_DATA,
  POPULAR_VEHICLE_BRANDS,
  POPULAR_YEARS,
  ENGINE_VARIANTS,
} from '@/constants/parts-taxonomy'

// Category Icons Mapping
const CATEGORY_ICONS: Record<string, any> = {
  'engine-mechanical': Zap,
  'transmission-clutch': Layers,
  brakes: Disc,
  'suspension-steering': Wrench,
  'electrical-electronics': Cpu,
  'cooling-hvac': Thermometer,
  'body-exterior': Car,
  'interior-parts': Armchair,
  'fuel-exhaust': Fuel,
  'wheels-tyres': CircleDot,
  'filters-service-parts': Filter,
}

export function FindMyPartSection() {
  const navigate = useNavigate()

  // ── Stepper State ────────────────────────────────────────────────────────
  const [selectedBrand, setSelectedBrand] = useState('Maruti Suzuki')
  const [selectedModel, setSelectedModel] = useState('Swift')
  const [selectedYear, setSelectedYear] = useState('2019')
  const [selectedVariant, setSelectedVariant] = useState('1.2L Naturally Aspirated Petrol')
  const [selectedCategory, setSelectedCategory] = useState(PARTS_CATEGORIES_DATA[0].name)

  // Dynamic models based on selected brand
  const brandModels = useMemo(() => {
    const found = POPULAR_VEHICLE_BRANDS.find((b) => b.brand.toLowerCase() === selectedBrand.toLowerCase())
    return found ? found.models : ['Standard Model']
  }, [selectedBrand])

  const handleSearchParts = () => {
    const params = new URLSearchParams()
    if (selectedBrand) params.set('brand', selectedBrand)
    if (selectedModel) params.set('model', selectedModel)
    if (selectedCategory) params.set('category', selectedCategory)
    navigate(`${appRoutes.parts}?${params.toString()}`)
  }

  const handleCategoryClick = (categoryName: string) => {
    navigate(`${appRoutes.parts}?category=${encodeURIComponent(categoryName)}`)
  }

  return (
    <section className="relative py-14 overflow-hidden bg-gradient-to-b from-background via-card/40 to-background border-y border-border/60">
      {/* Ambient background glows */}
      <div
        aria-hidden="true"
        className="absolute top-1/4 -left-40 size-96 rounded-full bg-[#CF2F2F]/10 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-10 -right-40 size-96 rounded-full bg-blue-500/5 blur-3xl pointer-events-none"
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-12 relative z-10">
        {/* ── Heading Row ───────────────────────────────────────────────── */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-[#CF2F2F]/15 text-[#CF2F2F] border border-[#CF2F2F]/30 font-bold uppercase text-[10px] tracking-wider px-2.5 py-0.5">
                Precision Compatibility
              </Badge>
              <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                <ShieldCheck className="size-3.5 text-emerald-500" /> 100% Genuine OEM &amp; Aftermarket
              </span>
            </div>

            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold text-foreground tracking-tight uppercase">
              FIND MY <span className="text-[#CF2F2F]">CAR PART</span>
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-xl">
              Match genuine spares, performance tuning parts, and OEM replacements tailored specifically to your exact vehicle model and engine configuration.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate(appRoutes.parts)}
            className="inline-flex items-center gap-2 text-xs font-bold text-[#CF2F2F] hover:text-[#CF2F2F]/80 uppercase tracking-wider transition-colors shrink-0 group cursor-pointer"
          >
            <span>Explore All Parts</span>
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>

        {/* ── 5-Step Precision Finder Tool Card ─────────────────────────── */}
        <div className="rounded-2xl border border-border/80 bg-card/90 backdrop-blur-xl p-5 sm:p-7 shadow-xl space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="flex size-7 items-center justify-center rounded-lg bg-[#CF2F2F]/10 text-[#CF2F2F]">
                <Sparkles className="size-4" />
              </div>
              <h3 className="font-display text-sm sm:text-base font-bold text-foreground">
                5-Step Vehicle Matcher
              </h3>
            </div>
            <span className="text-[11px] font-mono text-muted-foreground">
              Guaranteed Fitment for Indian &amp; Imported Cars
            </span>
          </div>

          {/* Stepper Inputs Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
            {/* Step 1: Vehicle Brand */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
                1. Vehicle Brand
              </label>
              <select
                value={selectedBrand}
                onChange={(e) => {
                  const b = e.target.value
                  setSelectedBrand(b)
                  const found = POPULAR_VEHICLE_BRANDS.find((brand) => brand.brand === b)
                  if (found) setSelectedModel(found.models[0])
                }}
                className="w-full h-11 rounded-xl border border-border bg-background px-3 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-[#CF2F2F]/50 transition-all cursor-pointer"
              >
                {POPULAR_VEHICLE_BRANDS.map((b) => (
                  <option key={b.brand} value={b.brand}>
                    {b.brand}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 2: Vehicle Model */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
                2. Model
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full h-11 rounded-xl border border-border bg-background px-3 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-[#CF2F2F]/50 transition-all cursor-pointer"
              >
                {brandModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 3: Manufacturing Year */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
                3. Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full h-11 rounded-xl border border-border bg-background px-3 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-[#CF2F2F]/50 transition-all cursor-pointer"
              >
                {POPULAR_YEARS.slice(0, 16).map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 4: Engine Variant */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
                4. Engine Variant
              </label>
              <select
                value={selectedVariant}
                onChange={(e) => setSelectedVariant(e.target.value)}
                className="w-full h-11 rounded-xl border border-border bg-background px-3 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-[#CF2F2F]/50 transition-all truncate cursor-pointer"
              >
                {ENGINE_VARIANTS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 5: Part Category */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
                5. Part Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full h-11 rounded-xl border border-border bg-background px-3 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-[#CF2F2F]/50 transition-all truncate cursor-pointer"
              >
                {PARTS_CATEGORIES_DATA.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Row */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
              <span>
                Matching parts for: <strong className="text-foreground">{selectedBrand} {selectedModel} ({selectedYear})</strong>
              </span>
            </div>

            <Button
              type="button"
              onClick={handleSearchParts}
              className="w-full sm:w-auto bg-[#CF2F2F] hover:bg-[#CF2F2F]/90 text-white font-bold gap-2 text-xs px-6 py-2.5 rounded-xl shadow-glow cursor-pointer"
            >
              <Search className="size-4" />
              <span>Find Matching Parts</span>
            </Button>
          </div>
        </div>

        {/* ── 11 Standard Categories Showcase Grid ──────────────────────── */}
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-border/60 pb-3">
            <h3 className="font-display text-lg sm:text-xl font-extrabold text-foreground uppercase tracking-tight">
              Explore By Automotive <span className="text-[#CF2F2F]">Category</span>
            </h3>
            <span className="text-xs text-muted-foreground font-semibold">11 Certified Systems</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {PARTS_CATEGORIES_DATA.map((category) => {
              const IconComponent = CATEGORY_ICONS[category.id] || Wrench

              return (
                <div
                  key={category.id}
                  onClick={() => handleCategoryClick(category.name)}
                  className="group relative rounded-2xl border border-border/80 bg-card hover:border-[#CF2F2F]/40 p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg cursor-pointer flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-[#CF2F2F] group-hover:text-white transition-colors duration-200">
                        <IconComponent className="size-5" />
                      </div>
                      <ChevronRight className="size-4 text-muted-foreground group-hover:text-[#CF2F2F] group-hover:translate-x-1 transition-all" />
                    </div>

                    <div>
                      <h4 className="font-display text-sm font-bold text-foreground group-hover:text-[#CF2F2F] transition-colors line-clamp-1">
                        {category.name}
                      </h4>
                      <p className="text-[11px] text-muted-foreground line-clamp-2 mt-1 leading-relaxed">
                        {category.description}
                      </p>
                    </div>

                    {/* Sub-parts Preview Tags */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {category.subParts.slice(0, 3).map((sub) => (
                        <span
                          key={sub}
                          className="text-[10px] bg-muted/60 text-muted-foreground px-2 py-0.5 rounded-md font-medium group-hover:text-foreground transition-colors"
                        >
                          {sub}
                        </span>
                      ))}
                      {category.subParts.length > 3 && (
                        <span className="text-[10px] text-muted-foreground/70 font-semibold px-1 py-0.5">
                          +{category.subParts.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border/40 mt-4 flex items-center justify-between text-[11px] font-bold text-[#CF2F2F]">
                    <span>View Parts Catalog</span>
                    <ArrowRight className="size-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
